// Angular modules.
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
// Zenklub interfaces.
import {
	IAvailability,
	IAvailabilityApi,
	IWeekdaySlot,
	IWeekdaySlotItem,
} from 'app/interfaces/availability.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
// Zenklub services.
import { AvailabilitiesService } from 'app/services/availabilities.service';
import { DateTimeService } from 'app/services/datetime.service';
import { UserService } from 'app/services/user.service';
import * as $ from 'jquery';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';

@Component({
	selector: 'app-manage-availabilities',
	templateUrl: './manage-availabilities.component.html',
	styleUrls: ['./manage-availabilities.component.scss'],
})
export class ManageAvailabilitiesComponent implements OnInit {
	// Inputs.
	@Input() slotTimezone: string;
	@Input() newPeriodDate: Date[] = [];
	@Input() newPeriodDateFrom: Date;
	@Input() newPeriodDateTo: Date;
	@Input() availabilityForEdit: IAvailabilityApi;
	@Input() isAddNew: boolean;
	@Input() resetProfileCompletion: ReplaySubject<boolean>;

	// Outputs.
	@Output() editMode: EventEmitter<any> = new EventEmitter<any>();

	maxInputDate = new Date();
	// User's preferences.
	userPref: IUserPreferences;
	// Status.
	isLoading = false;
	// Calendar data.
	calendarLabels: any;
	hoursList: string[] = this.dateTime.buildDailyHours(true);
	availableDays: number[] = [];

	// Avaliability slots.
	slots: IAvailability;

	// Translated strings.
	strAreYouSure: string;
	strToDateMustBeGreater: string;
	strToDateMustBeLower: string;

	// Constructor method.
	constructor(
		private availabilities: AvailabilitiesService,
		private dateTime: DateTimeService,
		private translate: TranslateService,
		protected notifications: ZkNotificationService,
		private userService: UserService
	) {
		const now = new Date();
		this.maxInputDate = new Date(
			now.getFullYear(),
			now.getMonth() + 3,
			now.getDate()
		);
	}

	// On init.
	ngOnInit(): void {
		this.newPeriodDate = [this.newPeriodDateFrom, this.newPeriodDateTo];

		// Build available weekdays.
		this.updateAvailableWeekdays();

		// Get user preferences.
		this.userPref = this.userService.getUserLocalPreferences();

		// If in edition mode.
		if (this.availabilityForEdit) {
			this.createSlotFromData(this.availabilityForEdit);
		} else {
			this.slots = {
				timezone: this.slotTimezone,
				weekdaySlots: [],
			};
		}
		// Determine calendar modes and labels.
		this.translate
			.get([
				'CALENDAR.MODES.MONTH',
				'CALENDAR.MODES.WEEK',
				'CALENDAR.MODES.DAY',
				'CALENDAR.MONTHS',
				'CALENDAR.WEEKDAYS',
				'MESSAGES.AREYOUSURE',
				'MESSAGES.TODATEMUSTBEGREATER',
				'MESSAGES.TODATEMUSTBELOWER',
			])
			.subscribe((res) => {
				// Calendar labels.
				const cMon: any = res['CALENDAR.MONTHS'];
				const cWee: any = res['CALENDAR.WEEKDAYS'];
				this.calendarLabels = {
					firstDayOfWeek: 0,
					dayNames: [
						cWee.SUN.SHORT,
						cWee.MON.SHORT,
						cWee.TUE.SHORT,
						cWee.WED.SHORT,
						cWee.THU.SHORT,
						cWee.FRI.SHORT,
						cWee.SAT.SHORT,
					],
					dayNamesShort: [
						cWee.SUN.ABB,
						cWee.MON.ABB,
						cWee.TUE.ABB,
						cWee.WED.ABB,
						cWee.THU.ABB,
						cWee.FRI.ABB,
						cWee.SAT.ABB,
					],
					monthNames: [
						cMon.JAN.FULL,
						cMon.FEB.FULL,
						cMon.MAR.FULL,
						cMon.APR.FULL,
						cMon.MAY.FULL,
						cMon.JUN.FULL,
						cMon.JUL.FULL,
						cMon.AUG.FULL,
						cMon.SEP.FULL,
						cMon.OCT.FULL,
						cMon.NOV.FULL,
						cMon.DEC.FULL,
					],
					monthNamesShort: [
						cMon.JAN.ABB,
						cMon.FEB.ABB,
						cMon.MAR.ABB,
						cMon.APR.ABB,
						cMon.MAY.ABB,
						cMon.JUN.ABB,
						cMon.JUL.ABB,
						cMon.AUG.ABB,
						cMon.SEP.ABB,
						cMon.OCT.ABB,
						cMon.NOV.ABB,
						cMon.DEC.ABB,
					],
				};
				// Strings.
				this.strAreYouSure = res['MESSAGES.AREYOUSURE'];
				this.strToDateMustBeGreater = res['MESSAGES.TODATEMUSTBEGREATER'];
				this.strToDateMustBeLower = res['MESSAGES.TODATEMUSTBELOWER'];
				// Position.
				this.onResize();
			});
	}

	// Create slot from server data.
	createSlotFromData(preSlot: IAvailabilityApi): void {
		this.slots = this.availabilities.buildFromServerData(preSlot);
		this.slots.weekdaySlots.forEach((wds) => {
			// Remove weekday from available days list.
			this.availableDays.forEach((day, index) => {
				if (day === wds.dayOfWeek) {
					this.availableDays.splice(index, 1);
				}
			});
		});

		if (this.isAddNew) {
			this.addDayBlock();
		}
	}

	// Add a new slot for a day.
	addDayBlock(
		startHours = 8,
		startMinutes = 0,
		endHours = 9,
		endMinutes = 0,
		dayOfWeek?: number
	): void {
		// Ensure weekday.
		if (!dayOfWeek) {
			dayOfWeek = this.availableDays.length > 0 ? this.availableDays[0] : 1;
		}

		// Remove weekday from available days list.
		this.availableDays.forEach((day, index) => {
			if (day === dayOfWeek) {
				this.availableDays.splice(index, 1);
			}
		});

		// Create new slot.
		const wdsItem: IWeekdaySlotItem = this.availabilities.createSlotItem(
			startHours,
			startMinutes,
			endHours,
			endMinutes
		);
		const tSlot: IWeekdaySlot = this.availabilities.createWeekdaySlot(
			dayOfWeek,
			[wdsItem]
		);

		if (this.slots) {
			this.slots.weekdaySlots.push(tSlot);
		} else {
			this.slots = {
				weekdaySlots: [tSlot],
				key:
					'ava.' +
					Math.floor(Math.random() * 1000).toString() +
					'.' +
					Date.now().toFixed(),
			};
		}
	}

	// Add an hour interval.
	addHourInterval(key: string): void {
		this.slots.weekdaySlots.forEach((slot, index) => {
			// Next start hours.
			let nextHours = 8;
			let nextMinutes = 0;

			// Find related weekday.
			if (slot.key === key) {
				// Get hour of sequence.
				if (slot.items.length > 0) {
					const lastItem: IWeekdaySlotItem = slot.items[slot.items.length - 1];
					nextHours = lastItem.endHours;
					nextMinutes = lastItem.endMinutes;
				}
				// Next end hours.
				const nextEndHours: number = nextHours < 23 ? nextHours + 1 : 0;
				const nextEndMinutes: number = nextMinutes;
				// New timeslot.
				const newSlotItem: IWeekdaySlotItem = {
					startHours: nextHours,
					startMinutes: nextMinutes,
					endHours: nextEndHours,
					endMinutes: nextEndMinutes,
					startString: this.dateTime.getHourString(nextHours, nextMinutes),
					endString: this.dateTime.getHourString(nextEndHours, nextEndMinutes),
					key:
						'slo.' +
						Math.floor(Math.random() * 100).toString() +
						'.' +
						Date.now().toFixed(),
				};
				// Append new slot.
				this.slots.weekdaySlots[index].items.push(newSlotItem);
			}
		});
	}

	// Remove an hour interval.
	removeHourInterval(item: string, key: string): void {
		this.slots.weekdaySlots.forEach((slot, index) => {
			if (slot.key === item) {
				slot.items.forEach((slotItem, i) => {
					if (slotItem.key === key) {
						this.slots.weekdaySlots[index].items.splice(i, 1);
					}
				});
				if (slot.items.length === 0) {
					this.slots.weekdaySlots.splice(index, 1);
				}
			}
		});

		if (this.slots.weekdaySlots.length === 0) {
			this.updateAvailableWeekdays();
		}
	}

	// When user select a different weekday.
	changeSelectedWeekday(key: string): void {
		// Get select changed.
		const select: JQuery<HTMLElement> = $(
			'td.tdTimetables[data-key="' + key + '"] select.selWeekday'
		);
		// New weekday.
		const newWeekday: number = parseInt(select.val().toString(), 10);
		// Change weekday.
		this.slots.weekdaySlots.forEach((oldSlot) => {
			if (oldSlot.key === key) {
				oldSlot.dayOfWeek = newWeekday;
			}
		});
	}

	// Cancel complete operation.
	cancelAll(): void {
		if (confirm(this.strAreYouSure)) {
			this.slots = null;
			this.newPeriodDate[0] = null;
			this.newPeriodDate[1] = null;
			this.editMode.emit(false);
		}
	}

	callSavedErrorMessage(e: any, res: any) {
		this.notifications.showError(
			e.error.messageI18n,
			res['MESSAGES.SAVEDERROR']
		);
	}

	// Save new slots.
	saveAll(): void {
		this.isLoading = true;

		this.slots.startDate = this.dateTime.toISOString(
			this.newPeriodDate[0],
			'startOfDay',
			this.slots.timezone
		);

		this.slots.endDate = this.dateTime.toISOString(
			this.newPeriodDate[1],
			'endOfDay',
			this.slots.timezone
		);

		// Try to save.
		this.translate
			.get(['MESSAGES.SAVEDSUCCESSFULLY', 'MESSAGES.SAVEDERROR'])
			.subscribe((res) => {
				this.availabilities.save(this.slots).subscribe(
					() => {
						this.isLoading = false;
						this.notifications.showSuccess(res['MESSAGES.SAVEDSUCCESSFULLY']);
						this.showAllAvailabilities();
						this.resetProfileCompletion.next(true);
					},
					(e) => {
						this.isLoading = false;
						this.callSavedErrorMessage(e, res);
					}
				);
			});
	}

	// Save updated slot.
	saveUpdates(): void {
		// Update dates.
		this.isLoading = true;
		this.slots.startDate = this.dateTime.toISOString(
			this.newPeriodDate[0],
			'startOfDay',
			this.slots.timezone
		);

		this.slots.endDate = this.dateTime.toISOString(
			this.newPeriodDate[1],
			'endOfDay',
			this.slots.timezone
		);

		// Try to update.
		this.translate
			.get(['MESSAGES.SAVEDSUCCESSFULLY', 'MESSAGES.SAVEDERROR'])
			.subscribe((res) => {
				this.availabilities.edit(this.slots).subscribe(
					() => {
						this.isLoading = false;
						this.notifications.showSuccess(res['MESSAGES.SAVEDSUCCESSFULLY']);
						this.showAllAvailabilities();
						this.resetProfileCompletion.next(true);
					},
					(e) => {
						this.isLoading = false;
						this.notifications.showError(
							e.error.messageI18n,
							res['MESSAGES.SAVEDERROR']
						);
					}
				);
			});
	}

	// Update available weekdays.
	updateAvailableWeekdays(): void {
		this.availableDays = [1, 2, 3, 4, 5, 6, 0];
	}

	// When a date is selected on period interval form.
	periodHourSelected(selDate: Date, item: number): void {
		if (item === 1) {
			if (selDate < this.newPeriodDate[0]) {
				this.notifications.showWarning(this.strToDateMustBeGreater);
				this.newPeriodDate[1] = this.newPeriodDate[0];
			} else if (selDate > this.maxInputDate) {
				this.notifications.showWarning(this.strToDateMustBeLower);
				this.newPeriodDate[1] = this.maxInputDate;
			}
		} else {
			if (
				!this.newPeriodDate[1] ||
				this.newPeriodDate[1] < this.newPeriodDate[0]
			) {
				this.newPeriodDate[1] = this.newPeriodDate[0];
			}
		}
	}

	// When an hour is selected.
	fromHourSelected(parentKey: string, key: string): void {
		// Get selects.
		const selectFrom: JQuery<HTMLElement> = $(
			'div.divTimeTableItem[data-key="' + key + '"] select.selHourSlotFrom'
		);
		const selectTo: JQuery<HTMLElement> = $(
			'div.divTimeTableItem[data-key="' + key + '"] select.selHourSlotTo'
		);
		// Selected from hour.
		const hourFrom: string = selectFrom.val().toString();
		const hourTo: string = selectTo.val().toString();
		const hF: number = parseInt(hourFrom.split(':')[0], 10);
		const mF: number = parseInt(hourFrom.split(':')[1], 10);
		let hT: number = parseInt(hourTo.split(':')[0], 10);
		let mT: number = parseInt(hourTo.split(':')[1], 10);

		if (hourFrom >= hourTo && hF + mF !== 0 && hT !== 0) {
			hT = hF < 23 ? hF + 1 : 0;
			mT = mF;
		}

		this.slots.weekdaySlots.forEach((slot) => {
			if (slot.key === parentKey) {
				slot.items.forEach((item) => {
					if (item.key === key) {
						item.startHours = hF;
						item.startMinutes = mF;
						item.endHours = hT;
						item.endMinutes = mT;
						item.startString = this.dateTime.getHourString(hF, mF);
						item.endString = this.dateTime.getHourString(hT, mT);
						selectFrom.val(item.startString);
						selectTo.val(item.endString);
					}
				});
			}
		});
	}

	// Show all availabilities.
	showAllAvailabilities(): void {
		this.editMode.emit(false);
	}

	// On resize window.
	public onResize(): void {
		let tryAgain = false;

		const inpDtFrom: JQuery<HTMLElement> = $('#inpPeriodDateFrom');
		const inpDtIcon: JQuery<HTMLElement> = $('.iconCal');
		if (inpDtFrom.length > 0 && inpDtIcon.length > 0) {
			inpDtIcon.css('margin-left', inpDtFrom.width());
		} else {
			tryAgain = true;
		}
		if (tryAgain) {
			setTimeout(() => {
				this.onResize();
			}, 200);
		}
	}
}
